import type { ChartLineConfig } from "@tellescope/ui";
import type { VisualizationResult } from "../types";

interface SingleStatData {
  type: "single-stat";
  value: number | string;
  label: string | null;
}

interface PieData {
  type: "pie-chart";
  data: Array<{ name: string; value: number }>;
}

interface BarData {
  type: "bar-chart";
  data: Array<Record<string, unknown>>;
  categoryKey: string;
  dataKey: string;
  additionalDataKeys: string[];
}

interface TimeSeriesData {
  type: "time-series";
  data: Array<Record<string, unknown>>;
  xAxisKey: string;
  lines: ChartLineConfig[];
}

interface TableData {
  type: "table";
  data: Array<Record<string, unknown>>;
  columns: string[];
}

export type ChartData = SingleStatData | PieData | BarData | TimeSeriesData | TableData | null;

const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "-";
  }
  if (typeof value === "number") {
    return value.toLocaleString();
  }
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

export const useAskAIVisualizationLogic = (
  visualization: VisualizationResult
): { chartData: ChartData; formatCellValue: (value: unknown) => string } => {
  const { inference, data } = visualization;

  const chartData = ((): ChartData => {
    switch (inference.type) {
      case "single-stat": {
        if (!inference.dataKey || data.length === 0) return null;
        const firstItem = data[0];
        if (!firstItem) return null;
        const value = firstItem[inference.dataKey];
        // If there's a labelKey (e.g., _id from a group aggregation), use its value as the label
        // This handles cases like "most common first name" where the result is { _id: "John", count: 42 }
        // or { _id: null, count: 382 } for missing values (label will be null, component handles translation)
        let label: string | null;
        if (inference.labelKey && inference.labelKey in firstItem) {
          const labelValue = firstItem[inference.labelKey];
          label = labelValue === null || labelValue === undefined ? null : String(labelValue);
        } else {
          label = inference.dataKey.charAt(0).toUpperCase() + inference.dataKey.slice(1);
        }
        return {
          type: "single-stat",
          value: typeof value === "number" || typeof value === "string" ? value : String(value),
          label,
        };
      }
      case "pie-chart": {
        if (!inference.labelKey || !inference.dataKey) return null;
        return {
          type: "pie-chart",
          data: data.map((item) => ({
            name: String(item[inference.labelKey!] ?? "Unknown"),
            value: Number(item[inference.dataKey!] ?? 0),
          })),
        };
      }
      case "bar-chart": {
        if (!inference.labelKey || !inference.dataKey) return null;
        return {
          type: "bar-chart",
          data,
          categoryKey: inference.labelKey,
          dataKey: inference.dataKey,
          additionalDataKeys: inference.valueKeys?.filter((k) => k !== inference.dataKey) ?? [],
        };
      }
      case "time-series": {
        if (!inference.timestampKey || !inference.valueKeys || inference.valueKeys.length === 0) return null;
        return {
          type: "time-series",
          data,
          xAxisKey: inference.timestampKey,
          lines: inference.valueKeys.map((key) => ({
            key,
            label: key.charAt(0).toUpperCase() + key.slice(1),
          })),
        };
      }
      case "table":
      default: {
        if (data.length === 0) return null;
        const firstRow = data[0];
        return {
          type: "table",
          data,
          columns: firstRow ? Object.keys(firstRow) : [],
        };
      }
    }
  })();

  return { chartData, formatCellValue };
};
